/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { POPUP_POSITION } from "constants/enums";

export const getPopupApis = stores => {
  /*
   * Displays a popup that hosts a component from a specific dropin.
   * @param {string} dropinId - The ID of the dropin to which the component belongs, for which the popup should be displayed.
   * @param {object} options - The options for the popup.
   * available options:
   * position: The position of the popup. Can be either POPUP_POSITION.TOP_RIGHT or POPUP_POSITION.BOTTOM_RIGHT.
   * onClose: Function to be called when the popup is closed.
   * hideOnCloseButtonClick: Function to be called to stop the popup from being hidden.
   *
   * @returns {boolean} - Returns true if the API decides the popup should be displayed, false otherwise. It does not wait for the popup to be displayed successfully.
   * @example
   * showPopup("dropinId", {
   *  position: POPUP_POSITION.TOP_RIGHT,
   *  onClose: () => {},
   *  hideOnCloseButtonClick: () => Promise.resolve(true)
   * });
   * The dropin that has the component to be displayed in the popup should implement the getCustomDialogComponent method in its bootstrap process.
   */
  function showPopup(
    dropinId,
    options = {
      position: POPUP_POSITION.TOP_RIGHT,
      onClose: () => {},
      hideOnCloseButtonClick: () => Promise.resolve(true)
    }
  ) {
    const args = {
      dropinId,
      ...options
    };

    // If there is already a popup being shown, return false
    const shouldShowPopup = !stores.appStore.shouldShowPopup;
    if (shouldShowPopup) {
      stores.appStore.setPopupConfig(args);
      stores.appStore.setShouldShowPopup(true);
    }

    return shouldShowPopup;
  }

  /*
   * Hides the popup that hosts a component from a specific dropin.
   * @param {string} dropinId - The ID of the dropin to which the component belongs, for which the popup should be hidden.
   * @returns {boolean} - Returns true if the API decides the popup should be hidden, false otherwise. It does not wait for the popup to be hidden successfully.
   * @example
   * hidePopup("dropinId");
   */
  function hidePopup(dropinId) {
    // If the popup being hidden is not the one being shown, return false
    const shouldHidePopup =
      stores.appStore.shouldShowPopup === true && stores.appStore.popupConfig.dropinId === dropinId;
    if (shouldHidePopup) {
      stores.appStore.setShouldShowPopup(false);
      stores.appStore.setPopupConfig({});
    }

    return shouldHidePopup;
  }

  return {
    showPopup,
    hidePopup
  };
};
